import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DollarSign, Leaf, TrendingDown, Users, Globe, Wifi, Server, Cloud } from 'lucide-react';
import { costApi, awsApi, optimizationApi } from '../services/api';
import { Card, SectionHeader, Loader, StatCard, EmptyState, Badge } from '../components/ui';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  AreaChart, Area, LineChart, Line, PieChart, Pie,
} from 'recharts';

const DIM_COLORS = {
  compute: '#0084ff',
  database: '#a78bfa',
  network: '#00c896',
  licensing: '#ffb800',
  energy: '#34d399',
  facility: '#fb923c',
  operations: '#f472b6',
};

const TEAMS = ['All Teams', 'Platform', 'Backend', 'Frontend', 'Data', 'ML/AI', 'DevOps'];

function fmt(n) {
  if (n === null || n === undefined || n === 0) return 'No data';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;
  return `$${Number(n).toFixed(2)}`;
}

const tooltipStyle = {
  background: '#0d1527',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8,
  boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
};

const tableHeaderStyle = {
  textAlign: 'left',
  padding: '10px 12px',
  color: 'rgba(255,255,255,0.35)',
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
};

// Mock data for new sections
const MOCK_IDLE_RESOURCES = [
  { resource: 'staging-worker-04', type: 'EC2 m5.xlarge', provider: 'AWS', avgCpu: 3, dailyCost: 92, wasteEstimate: 2760, lastActive: '14 days ago' },
  { resource: 'dev-gpu-instance', type: 'EC2 p3.2xlarge', provider: 'AWS', avgCpu: 1, dailyCost: 245, wasteEstimate: 7350, lastActive: '30 days ago' },
  { resource: 'test-db-replica', type: 'RDS r5.large', provider: 'AWS', avgCpu: 5, dailyCost: 68, wasteEstimate: 2040, lastActive: '21 days ago' },
  { resource: 'old-cache-cluster', type: 'ElastiCache r6g', provider: 'AWS', avgCpu: 2, dailyCost: 55, wasteEstimate: 1650, lastActive: '45 days ago' },
  { resource: 'staging-lb-02', type: 'ALB', provider: 'AWS', avgCpu: 0, dailyCost: 18, wasteEstimate: 540, lastActive: '60 days ago' },
];

const MOCK_RI_OPPORTUNITIES = [
  { resource: 'prod-api-cluster', currentCost: 4200, riCost: 2520, savings: 1680, term: '1 year', type: 'Reserved Instance' },
  { resource: 'prod-worker-pool', currentCost: 3100, riCost: 1860, savings: 1240, term: '1 year', type: 'Reserved Instance' },
  { resource: 'batch-processing', currentCost: 1800, spotCost: 540, savings: 1260, term: 'Spot', type: 'Spot Instance' },
  { resource: 'ci-cd-runners', currentCost: 950, spotCost: 285, savings: 665, term: 'Spot', type: 'Spot Instance' },
];

const MOCK_DATA_TRANSFER = {
  interRegion: [
    { route: 'us-east-1 -> eu-west-1', cost: 3400, gbTransferred: 8500 },
    { route: 'us-east-1 -> ap-southeast-1', cost: 2800, gbTransferred: 5600 },
    { route: 'eu-west-1 -> us-west-2', cost: 1900, gbTransferred: 3800 },
    { route: 'us-east-1 -> us-west-2', cost: 1200, gbTransferred: 6000 },
    { route: 'ap-southeast-1 -> ap-northeast-1', cost: 890, gbTransferred: 1780 },
  ],
  cdnHitRatio: 94.2,
};

const MOCK_TEAM_COSTS = [
  { team: 'Platform', spend: 245000, budget: 280000, trend: -3.2, headcount: 12 },
  { team: 'Backend', spend: 189000, budget: 200000, trend: 2.1, headcount: 18 },
  { team: 'Data', spend: 156000, budget: 160000, trend: -1.5, headcount: 8 },
  { team: 'ML/AI', spend: 134000, budget: 150000, trend: 8.4, headcount: 6 },
  { team: 'Frontend', spend: 45000, budget: 60000, trend: -5.3, headcount: 10 },
  { team: 'DevOps', spend: 78000, budget: 90000, trend: -2.8, headcount: 5 },
];

const MOCK_CARBON_WORKLOADS = [
  { workload: 'API Servers', carbon: 120 },
  { workload: 'Databases', carbon: 95 },
  { workload: 'ML Training', carbon: 180 },
  { workload: 'CI/CD', carbon: 45 },
  { workload: 'Storage', carbon: 30 },
];

const MOCK_RENEWABLE_TREND = [
  { month: 'Jul', pct: 58 },
  { month: 'Aug', pct: 62 },
  { month: 'Sep', pct: 65 },
  { month: 'Oct', pct: 68 },
  { month: 'Nov', pct: 72 },
  { month: 'Dec', pct: 76 },
];

export default function CostReportsPage() {
  const [teamFilter, setTeamFilter] = useState('All Teams');
  const [envFilter, setEnvFilter] = useState('All');

  const { data: dashboard, isLoading: loadingDash } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => costApi.getDashboard().then((r) => r.data),
  });

  const { data: reports = [], isLoading: loadingReports } = useQuery({
    queryKey: ['reports'],
    queryFn: () => costApi.getReports(12).then((r) => r.data),
  });

  const { data: awsCurrent } = useQuery({
    queryKey: ['aws-current-month'],
    queryFn: () => awsApi.getCurrentMonth().then((r) => r.data),
  });

  const { data: awsForecast } = useQuery({
    queryKey: ['aws-forecast'],
    queryFn: () => awsApi.getForecast().then((r) => r.data),
  });

  const { data: awsDaily } = useQuery({
    queryKey: ['aws-daily-trend'],
    queryFn: () => awsApi.getDailyTrend().then((r) => r.data),
  });

  const { data: awsServiceData } = useQuery({
    queryKey: ['aws-by-service-3'],
    queryFn: () => awsApi.getCostByService(3).then((r) => r.data),
  });

  const { data: awsRightsizing } = useQuery({
    queryKey: ['aws-rightsizing'],
    queryFn: () => awsApi.getRightsizing().then((r) => r.data),
  });

  const { data: ec2Instances } = useQuery({
    queryKey: ['aws-ec2'],
    queryFn: () => awsApi.getEc2Instances().then((r) => r.data),
  });

  const { data: rdsInstances } = useQuery({
    queryKey: ['aws-rds'],
    queryFn: () => awsApi.getRdsInstances().then((r) => r.data),
  });

  const { data: liveAnalysis } = useQuery({
    queryKey: ['live-analysis-cost'],
    queryFn: () => optimizationApi.getLiveAnalysis().then((r) => r.data),
  });

  const liveSavings = liveAnalysis?.totalPotentialSavings ? parseFloat(liveAnalysis.totalPotentialSavings) : 0;

  if (loadingDash || loadingReports) return <Loader />;

  const spendByDim = dashboard?.spendByDimension || {};
  const dimChartData = Object.entries(spendByDim).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value: Number(value),
    fill: DIM_COLORS[name] || '#0084ff',
  }));

  // Use LIVE AWS billing data (tenant-aware)
  const awsCurrentCost = awsCurrent?.blendedCost ? parseFloat(awsCurrent.blendedCost) : 0;
  const awsForecastCost = awsForecast?.forecast ? parseFloat(awsForecast.forecast) : 0;
  const currentSpend = awsCurrentCost;
  const projectedSpend = awsForecastCost || currentSpend;

  // Get previous month from historical service data
  const serviceMonths = awsServiceData?.months || [];
  const previousMonthData = serviceMonths.length >= 2 ? serviceMonths[serviceMonths.length - 2] : null;
  const previousSpend = previousMonthData
    ? previousMonthData.services.reduce((s, sv) => s + parseFloat(sv.cost || 0), 0)
    : awsForecastCost > 0 ? awsForecastCost : 0;

  const totalSavings = liveSavings > 0 ? liveSavings : (dashboard?.savingsAchieved || 0);

  const selectStyle = {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: 'rgba(255,255,255,0.7)',
    borderRadius: 8,
    padding: '7px 14px',
    fontSize: 12,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };

  return (
    <div>
      <SectionHeader
        title="Cost Reports & Analytics"
        subtitle="Unified view of infrastructure spend across all 7 cost dimensions"
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <select value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} style={selectStyle}>
              {TEAMS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={envFilter} onChange={(e) => setEnvFilter(e.target.value)} style={selectStyle}>
              {['All', 'production', 'staging', 'dev'].map((e) => <option key={e} value={e}>{e === 'All' ? 'All Envs' : e}</option>)}
            </select>
          </div>
        }
      />

      {/* KPI Row - Current/Previous/Projected */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 16,
        marginBottom: 24,
      }}>
        <StatCard
          label="Annual Cloud Spend"
          value={previousSpend > 0 ? fmt(previousSpend * 12) : 'No data'}
          subValue={previousSpend > 0 ? `Based on last month: ${fmt(previousSpend)}` : ''}
          icon={DollarSign}
          accentColor="#0084ff"
          trend={previousSpend > 0 && currentSpend > 0 ? -((previousSpend - currentSpend) / previousSpend * 100) : null}
        />
        <StatCard
          label="Current MTD"
          value={currentSpend > 0 ? fmt(currentSpend) : 'No data'}
          subValue={currentSpend > 0 ? `${awsCurrent?.estimated ? 'Estimated' : 'Actual'}` : ''}
          accentColor="rgba(255,255,255,0.3)"
        />
        <StatCard
          label="Annual Forecast"
          value={previousSpend > 0 ? fmt((previousSpend * 12) - (liveSavings * 12)) : 'No data'}
          subValue={previousSpend > 0 ? `Spend ${fmt(previousSpend * 12)} - Savings ${fmt(liveSavings * 12)}` : ''}
          accentColor="#ffb800"
          trend={previousSpend > 0 && liveSavings > 0 ? -((liveSavings / previousSpend) * 100) : null}
        />
        <StatCard
          label="Annual Savings Potential"
          value={totalSavings > 0 ? fmt(totalSavings * 12) : 'No data'}
          icon={TrendingDown}
          accentColor="#00c896"
          trend={totalSavings > 0 ? 18 : null}
        />
      </div>

      {/* AWS Live Billing */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: 'rgba(255,153,0,0.15)', border: '1px solid rgba(255,153,0,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Cloud size={18} color="#ff9900" />
          </div>
          <div>
            <div style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>AWS Cost Explorer (Live)</div>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>Real billing data from your AWS account</div>
          </div>
        </div>

        {/* AWS KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
          <div style={{ background: 'rgba(255,153,0,0.06)', borderRadius: 10, padding: 14, border: '1px solid rgba(255,153,0,0.12)' }}>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Month-to-Date</div>
            <div style={{ color: '#ff9900', fontSize: 20, fontWeight: 700, marginTop: 4 }}>
              ${awsCurrent?.blendedCost ? Number(awsCurrent.blendedCost).toFixed(2) : '—'}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, marginTop: 2 }}>{awsCurrent?.period || ''}</div>
          </div>
          <div style={{ background: 'rgba(255,184,0,0.06)', borderRadius: 10, padding: 14, border: '1px solid rgba(255,184,0,0.12)' }}>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Forecasted</div>
            <div style={{ color: '#ffb800', fontSize: 20, fontWeight: 700, marginTop: 4 }}>
              ${awsForecast?.forecastedTotal ? Number(awsForecast.forecastedTotal).toFixed(2) : '—'}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, marginTop: 2 }}>End of month</div>
          </div>
          <div style={{ background: 'rgba(74,158,255,0.06)', borderRadius: 10, padding: 14, border: '1px solid rgba(74,158,255,0.12)' }}>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Usage Units</div>
            <div style={{ color: '#4a9eff', fontSize: 20, fontWeight: 700, marginTop: 4 }}>
              {awsCurrent?.usageQuantity ? Number(awsCurrent.usageQuantity).toFixed(0) : '—'}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, marginTop: 2 }}>Total this month</div>
          </div>
          <div style={{ background: 'rgba(0,200,150,0.06)', borderRadius: 10, padding: 14, border: '1px solid rgba(0,200,150,0.12)' }}>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Rightsizing</div>
            <div style={{ color: '#00c896', fontSize: 20, fontWeight: 700, marginTop: 4 }}>
              {awsRightsizing?.totalRecommendations ?? '—'}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, marginTop: 2 }}>Recommendations</div>
          </div>
        </div>

        {/* Daily trend + Service breakdown */}
        <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 20 }}>
          <div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 600, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Daily Cost Trend
            </div>
            {awsDaily?.dailyCosts?.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={awsDaily.dailyCosts.map(d => ({ date: d.date.slice(5), cost: Number(d.cost) }))}>
                  <defs>
                    <linearGradient id="gradAwsCost" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ff9900" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#ff9900" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v.toFixed(2)}`} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`$${Number(v).toFixed(2)}`, 'Cost']} />
                  <Area type="monotone" dataKey="cost" stroke="#ff9900" strokeWidth={2} fill="url(#gradAwsCost)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.2)', fontSize: 13 }}>
                Loading...
              </div>
            )}
          </div>

          <div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 600, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Cost by AWS Service
            </div>
            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
              {(() => {
                const months = awsServiceData?.months || [];
                const latest = months[months.length - 1];
                const services = (latest?.services || []).filter(s => Number(s.cost) > 0);
                if (services.length === 0) return <div style={{ color: 'rgba(255,255,255,0.2)', fontSize: 12, textAlign: 'center', padding: 20 }}>No costs</div>;
                const total = services.reduce((s, x) => s + Number(x.cost), 0);
                return services.map((s, i) => {
                  const pct = total > 0 ? (Number(s.cost) / total * 100) : 0;
                  const barColors = ['#ff9900', '#ffb800', '#4a9eff', '#00c896', '#a78bfa', '#fb923c', '#f472b6'];
                  return (
                    <div key={i} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                        <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.service.replace('Amazon ', '').replace('AWS ', '')}
                        </span>
                        <span style={{ color: '#fff', fontSize: 11, fontWeight: 600 }}>${Number(s.cost).toFixed(2)}</span>
                      </div>
                      <div style={{ height: 4, background: 'rgba(255,255,255,0.07)', borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: barColors[i % barColors.length], borderRadius: 2 }} />
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      </Card>

      {/* AWS Infrastructure - EC2 & RDS */}
      {((ec2Instances && ec2Instances.length > 0) || (rdsInstances && rdsInstances.length > 0)) && (
        <Card style={{ marginBottom: 24 }}>
          <div style={{ color: '#fff', fontWeight: 600, fontSize: 15, marginBottom: 18 }}>
            AWS Infrastructure Inventory
          </div>

          {ec2Instances && ec2Instances.length > 0 && (
            <>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 600, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                EC2 Instances ({ec2Instances.length})
              </div>
              <div style={{ overflowX: 'auto', marginBottom: 20 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Name', 'Instance ID', 'Type', 'State', 'AZ', 'Launch Time'].map(h => (
                        <th key={h} style={tableHeaderStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ec2Instances.map((inst, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <td style={{ padding: '10px 12px', color: '#fff', fontSize: 12, fontWeight: 500 }}>{inst.name || '—'}</td>
                        <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.6)', fontSize: 11, fontFamily: 'monospace' }}>{inst.instanceId}</td>
                        <td style={{ padding: '10px 12px', color: '#4a9eff', fontSize: 12 }}>{inst.instanceType}</td>
                        <td style={{ padding: '10px 12px' }}>
                          <span style={{
                            padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                            background: inst.state === 'running' ? 'rgba(0,200,150,0.15)' : 'rgba(255,255,255,0.08)',
                            color: inst.state === 'running' ? '#00c896' : 'rgba(255,255,255,0.5)',
                          }}>{inst.state}</span>
                        </td>
                        <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>{inst.availabilityZone}</td>
                        <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>{inst.launchTime?.slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {rdsInstances && rdsInstances.length > 0 && (
            <>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 600, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                RDS Instances ({rdsInstances.length})
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['DB Instance', 'Engine', 'Class', 'Status', 'Storage (GB)', 'Multi-AZ'].map(h => (
                        <th key={h} style={tableHeaderStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rdsInstances.map((db, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <td style={{ padding: '10px 12px', color: '#fff', fontSize: 12, fontWeight: 500, fontFamily: 'monospace' }}>{db.dbInstanceId}</td>
                        <td style={{ padding: '10px 12px', color: '#a78bfa', fontSize: 12 }}>{db.engine} {db.engineVersion}</td>
                        <td style={{ padding: '10px 12px', color: '#4a9eff', fontSize: 12 }}>{db.instanceClass}</td>
                        <td style={{ padding: '10px 12px' }}>
                          <span style={{
                            padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                            background: db.status === 'available' ? 'rgba(0,200,150,0.15)' : 'rgba(255,184,0,0.15)',
                            color: db.status === 'available' ? '#00c896' : '#ffb800',
                          }}>{db.status}</span>
                        </td>
                        <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>{db.allocatedStorageGB}</td>
                        <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>{db.multiAZ ? 'Yes' : 'No'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      )}

      {/* AWS Rightsizing Recommendations */}
      {awsRightsizing?.recommendations?.length > 0 && (
        <Card style={{ marginBottom: 24 }}>
          <div style={{ color: '#fff', fontWeight: 600, fontSize: 15, marginBottom: 18 }}>
            AWS Rightsizing Recommendations
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Instance ID', 'Current Type', 'Action', 'Target Type', 'Est. Savings'].map(h => (
                    <th key={h} style={tableHeaderStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {awsRightsizing.recommendations.map((rec, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.7)', fontSize: 11, fontFamily: 'monospace' }}>{rec.instanceId}</td>
                    <td style={{ padding: '10px 12px', color: '#4a9eff', fontSize: 12 }}>{rec.instanceType}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                        background: rec.action === 'Terminate' ? 'rgba(255,92,92,0.15)' : 'rgba(255,184,0,0.15)',
                        color: rec.action === 'Terminate' ? '#ff5c5c' : '#ffb800',
                      }}>{rec.action}</span>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#00c896', fontSize: 12 }}>{rec.targetInstanceType || '—'}</td>
                    <td style={{ padding: '10px 12px', color: '#00c896', fontSize: 13, fontWeight: 700 }}>{rec.estimatedMonthlySavings || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Spend by Dimension - Horizontal Bar Chart */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{
          color: '#fff',
          fontWeight: 600,
          fontSize: 15,
          marginBottom: 20,
        }}>
          Spend by Cost Dimension
        </div>
        {dimChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={dimChartData} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.05)"
                horizontal={false}
              />
              <XAxis
                type="number"
                tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => fmt(v)}
              />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 13 }}
                axisLine={false}
                tickLine={false}
                width={95}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(v) => [fmt(v), 'Spend']}
              />
              <Bar
                dataKey="value"
                radius={[0, 6, 6, 0]}
                barSize={22}
              >
                {dimChartData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState message="Connect your cloud provider to see spend breakdown" />
        )}
      </Card>

      {/* Utilization Report - Idle Resources */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>Utilization Report</div>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, marginTop: 2 }}>Idle and underutilized resources wasting budget</div>
          </div>
          <Badge label={`${MOCK_IDLE_RESOURCES.length} idle resources`} type="HIGH" />
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Resource', 'Type', 'Provider', 'Avg CPU', 'Daily Cost', 'Monthly Waste', 'Last Active'].map((h) => (
                  <th key={h} style={tableHeaderStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MOCK_IDLE_RESOURCES.map((r, i) => (
                <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ padding: '12px', color: '#fff', fontSize: 13, fontWeight: 500, fontFamily: 'monospace' }}>{r.resource}</td>
                  <td style={{ padding: '12px', color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>{r.type}</td>
                  <td style={{ padding: '12px', color: '#4a9eff', fontSize: 12 }}>{r.provider}</td>
                  <td style={{ padding: '12px', color: '#ff5c5c', fontSize: 13, fontWeight: 600 }}>{r.avgCpu}%</td>
                  <td style={{ padding: '12px', color: '#ffb800', fontSize: 13, fontWeight: 600 }}>${r.dailyCost}</td>
                  <td style={{ padding: '12px', color: '#ff5c5c', fontSize: 13, fontWeight: 700 }}>{fmt(r.wasteEstimate)}</td>
                  <td style={{ padding: '12px', color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>{r.lastActive}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 14, padding: '12px 16px', background: 'rgba(255,92,92,0.08)', borderRadius: 8, border: '1px solid rgba(255,92,92,0.15)' }}>
          <span style={{ color: '#ff5c5c', fontSize: 13, fontWeight: 600 }}>
            Total estimated waste: {fmt(MOCK_IDLE_RESOURCES.reduce((s, r) => s + r.wasteEstimate, 0))}/month
          </span>
        </div>
      </Card>

      {/* RI & Spot Opportunities */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ color: '#fff', fontWeight: 600, fontSize: 15, marginBottom: 18 }}>
          RI & Spot Opportunities
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Resource', 'Type', 'Current Cost', 'Optimized Cost', 'Monthly Savings', 'Term'].map((h) => (
                  <th key={h} style={tableHeaderStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MOCK_RI_OPPORTUNITIES.map((r, i) => (
                <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ padding: '12px', color: '#fff', fontSize: 13, fontWeight: 500 }}>{r.resource}</td>
                  <td style={{ padding: '12px' }}>
                    <Badge label={r.type} type={r.type === 'Reserved Instance' ? 'PENDING' : 'APPROVED'} />
                  </td>
                  <td style={{ padding: '12px', color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>{fmt(r.currentCost)}</td>
                  <td style={{ padding: '12px', color: '#4a9eff', fontSize: 13, fontWeight: 600 }}>{fmt(r.riCost || r.spotCost)}</td>
                  <td style={{ padding: '12px', color: '#00c896', fontSize: 14, fontWeight: 700 }}>{fmt(r.savings)}</td>
                  <td style={{ padding: '12px', color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>{r.term}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 14, padding: '12px 16px', background: 'rgba(0,200,150,0.08)', borderRadius: 8, border: '1px solid rgba(0,200,150,0.15)' }}>
          <span style={{ color: '#00c896', fontSize: 13, fontWeight: 600 }}>
            Total potential savings: {fmt(MOCK_RI_OPPORTUNITIES.reduce((s, r) => s + r.savings, 0))}/month
          </span>
        </div>
      </Card>

      {/* Data Transfer Analysis */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ color: '#fff', fontWeight: 600, fontSize: 15, marginBottom: 18 }}>
          Data Transfer Analysis
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
          {/* Inter-region costs table */}
          <div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: 600, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Top 5 Expensive Data Paths
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Route', 'Monthly Cost', 'Data (GB)'].map((h) => (
                    <th key={h} style={tableHeaderStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MOCK_DATA_TRANSFER.interRegion.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.7)', fontSize: 12, fontFamily: 'monospace' }}>{r.route}</td>
                    <td style={{ padding: '10px 12px', color: '#ffb800', fontSize: 13, fontWeight: 600 }}>{fmt(r.cost)}</td>
                    <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>{r.gbTransferred.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* CDN Cache Hit Ratio Gauge */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: 600, marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              CDN Cache Hit Ratio
            </div>
            <div style={{ position: 'relative', width: 140, height: 140 }}>
              <svg viewBox="0 0 140 140" style={{ transform: 'rotate(-90deg)' }}>
                <circle cx="70" cy="70" r="58" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="12" />
                <circle cx="70" cy="70" r="58" fill="none" stroke="#00c896" strokeWidth="12"
                  strokeDasharray={`${2 * Math.PI * 58 * MOCK_DATA_TRANSFER.cdnHitRatio / 100} ${2 * Math.PI * 58}`}
                  strokeLinecap="round"
                />
              </svg>
              <div style={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
                textAlign: 'center',
              }}>
                <div style={{ color: '#00c896', fontSize: 28, fontWeight: 700 }}>{MOCK_DATA_TRANSFER.cdnHitRatio}%</div>
                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>Hit Ratio</div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Team Cost Attribution */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ color: '#fff', fontWeight: 600, fontSize: 15, marginBottom: 18 }}>
          Team Cost Attribution
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Team', 'Monthly Spend', 'Budget', 'Usage', 'Trend', 'Per Head'].map((h) => (
                  <th key={h} style={tableHeaderStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MOCK_TEAM_COSTS
                .filter((t) => teamFilter === 'All Teams' || t.team === teamFilter)
                .map((t, i) => {
                  const usage = (t.spend / t.budget * 100).toFixed(0);
                  const usageColor = usage > 90 ? '#ff5c5c' : usage > 75 ? '#ffb800' : '#00c896';
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <td style={{ padding: '12px', color: '#fff', fontSize: 13, fontWeight: 600 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Users size={14} color="#4a9eff" /> {t.team}
                        </span>
                      </td>
                      <td style={{ padding: '12px', color: '#fff', fontSize: 14, fontWeight: 700 }}>{fmt(t.spend)}</td>
                      <td style={{ padding: '12px', color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>{fmt(t.budget)}</td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, height: 6, background: 'rgba(255,255,255,0.07)', borderRadius: 3, overflow: 'hidden', maxWidth: 100 }}>
                            <div style={{ height: '100%', width: `${Math.min(usage, 100)}%`, background: usageColor, borderRadius: 3 }} />
                          </div>
                          <span style={{ color: usageColor, fontSize: 12, fontWeight: 600 }}>{usage}%</span>
                        </div>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{ color: t.trend < 0 ? '#00c896' : '#ff5c5c', fontSize: 12, fontWeight: 600 }}>
                          {t.trend > 0 ? '\u2191' : '\u2193'} {Math.abs(t.trend)}%
                        </span>
                      </td>
                      <td style={{ padding: '12px', color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>{fmt(t.spend / t.headcount)}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ESG & Savings Grid - Enhanced */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 16,
        marginBottom: 24,
      }}>
        {/* Enhanced ESG Card */}
        <Card>
          <div style={{
            color: '#fff',
            fontWeight: 600,
            fontSize: 15,
            marginBottom: 18,
          }}>
            ESG & Sustainability
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {[
              {
                label: 'Carbon Emissions',
                value: dashboard?.carbonEmissionsKg
                  ? `${(dashboard.carbonEmissionsKg / 1000).toFixed(2)}t CO\u2082e`
                  : '\u2014',
                color: '#34d399',
              },
              {
                label: 'Renewable Energy',
                value: `${dashboard?.renewableEnergyPercent?.toFixed(1) ?? '\u2014'}%`,
                color: '#00c896',
              },
              {
                label: 'YoY Emissions Reduction',
                value: '\u22128.5%',
                color: '#0084ff',
              },
            ].map(({ label, value, color }) => (
              <div
                key={label}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                }}
              >
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>{label}</span>
                <span style={{ color, fontSize: 16, fontWeight: 700 }}>{value}</span>
              </div>
            ))}
          </div>

          {/* Carbon per workload chart */}
          <div style={{ marginTop: 20 }}>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: 600, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Carbon per Workload (kg CO2e)
            </div>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={MOCK_CARBON_WORKLOADS}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="workload" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="carbon" fill="#34d399" radius={[4, 4, 0, 0]} name="Carbon (kg)" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Renewable Energy Trend */}
          <div style={{ marginTop: 16 }}>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: 600, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Renewable Energy Trend
            </div>
            <ResponsiveContainer width="100%" height={120}>
              <AreaChart data={MOCK_RENEWABLE_TREND}>
                <defs>
                  <linearGradient id="gradRenewable" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00c896" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#00c896" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis domain={[50, 100]} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} unit="%" />
                <Tooltip contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="pct" stroke="#00c896" fill="url(#gradRenewable)" strokeWidth={2} name="Renewable %" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Savings Potential Card */}
        <Card>
          <div style={{
            color: '#fff',
            fontWeight: 600,
            fontSize: 15,
            marginBottom: 18,
          }}>
            Savings Potential
          </div>
          {['Compute', 'Database', 'Network', 'Operations'].map((dim, i) => {
            const pct = [38, 44, 31, 42][i];
            const colors = Object.values(DIM_COLORS);
            return (
              <div key={dim} style={{ marginBottom: 16 }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 6,
                }}>
                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>{dim}</span>
                  <span style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>{pct}% potential</span>
                </div>
                <div style={{
                  height: 6,
                  background: 'rgba(255,255,255,0.07)',
                  borderRadius: 3,
                  overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%',
                    width: `${pct}%`,
                    background: colors[i],
                    borderRadius: 3,
                    transition: 'width 0.4s ease',
                  }} />
                </div>
              </div>
            );
          })}

          {/* Before/After Savings Chart */}
          <div style={{ marginTop: 24 }}>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: 600, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Before & After Optimization
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={[
                { dim: 'Compute', before: 320, after: 198 },
                { dim: 'Database', before: 180, after: 100 },
                { dim: 'Network', before: 95, after: 65 },
                { dim: 'Ops', before: 120, after: 70 },
              ]}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="dim" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}K`} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`$${v}K`]} />
                <Bar dataKey="before" fill="rgba(255,255,255,0.15)" radius={[4, 4, 0, 0]} name="Before" />
                <Bar dataKey="after" fill="#00c896" radius={[4, 4, 0, 0]} name="After" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Historical Reports Table */}
      {reports.length > 0 && (
        <Card>
          <div style={{
            color: '#fff',
            fontWeight: 600,
            fontSize: 15,
            marginBottom: 18,
          }}>
            Historical Reports
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Period', 'Total Spend', 'Compute', 'Database', 'Network', 'Savings', 'Carbon (t)'].map(
                    (h) => (
                      <th key={h} style={tableHeaderStyle}>{h}</th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr
                    key={r.id}
                    style={{
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                      transition: 'background 0.15s',
                    }}
                  >
                    <td style={{ padding: '12px', color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>{r.reportPeriod}</td>
                    <td style={{ padding: '12px', color: '#fff', fontSize: 13, fontWeight: 600 }}>{fmt(r.totalSpend)}</td>
                    <td style={{ padding: '12px', color: '#fff', fontSize: 13 }}>{fmt(r.computeCost)}</td>
                    <td style={{ padding: '12px', color: '#fff', fontSize: 13 }}>{fmt(r.databaseCost)}</td>
                    <td style={{ padding: '12px', color: '#fff', fontSize: 13 }}>{fmt(r.networkCost)}</td>
                    <td style={{ padding: '12px', color: '#00c896', fontSize: 13, fontWeight: 600 }}>{fmt(r.totalSavingsAchieved)}</td>
                    <td style={{ padding: '12px', color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                      {r.carbonEmissionsKg ? (r.carbonEmissionsKg / 1000).toFixed(2) : '\u2014'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
